/**
 * "This weekend near you" home strip — surfaces upcoming events for the
 * user's currently-selected country (via CountryPicker context), filtered
 * to the next 7 days. Auto-hides when there are no upcoming events.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useCountry } from "@/contexts/CountryContext";
import { Calendar, MapPin, ArrowRight } from "lucide-react";

function weekendWindow() {
  // From "now" to end of the coming Sunday (or 7 days out, whichever is later)
  // so we always show a rolling week of events.
  const now = new Date();
  const end = new Date(now);
  // Move to Sunday
  const daysUntilSunday = (7 - now.getDay()) % 7 || 7;
  end.setDate(now.getDate() + Math.max(daysUntilSunday, 3));
  end.setHours(23, 59, 59, 999);
  return { from: now.toISOString(), to: end.toISOString() };
}

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  } catch { return iso; }
}

export default function EventsWeekendStrip({ limit = 8 }) {
  const { country } = useCountry();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const { from, to } = weekendWindow();
    const p = { limit, date_from: from, date_to: to };
    if (country?.name) p.country = country.name;
    return p;
  }, [country?.name, limit]);

  useEffect(() => {
    setLoading(true);
    http.get("/events", { params })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [params]);

  if (!loading && items.length === 0) return null;

  const scopeLabel = country?.name ? `IN ${country.name.toUpperCase()}` : "WORLDWIDE";

  return (
    <section
      data-testid="events-weekend-strip"
      className="w-full border-y"
      style={{ background: "var(--wofy-surface)", borderColor: "var(--wofy-border)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
              <div className="overline" data-testid="weekend-strip-scope">THIS WEEKEND · {scopeLabel}</div>
            </div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              Show up. Belong. {country?.name ? "In your neighborhood." : "Somewhere in the world."}
            </h2>
            <p className="mt-2 max-w-2xl text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              {country?.name
                ? `Upcoming community events in ${country.name} over the next week. Tap the flag ↗ to change country.`
                : "Community events over the next week worldwide. Set a country in the picker to see just yours."}
            </p>
          </div>
          <Link
            to={country?.name ? `/events?country=${encodeURIComponent(country.name)}` : "/events"}
            data-testid="weekend-strip-see-all"
            className="wofy-btn-ghost text-sm"
          >
            See all events <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading…</div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-2" style={{ scrollbarWidth: "thin" }}>
            {items.map((ev) => (
              <Link
                key={ev.id}
                to={`/events/${ev.slug || ev.id}`}
                data-testid={`weekend-strip-item-${ev.slug || ev.id}`}
                className="shrink-0 w-64 rounded-2xl overflow-hidden border hover:shadow-lg transition-shadow group"
                style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}
              >
                <div
                  className="w-full h-36 bg-cover bg-center relative"
                  style={{
                    backgroundImage: `url(${ev.cover_image || (ev.images && ev.images[0]) || "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?auto=format&fit=crop&w=1200&q=80"})`,
                    backgroundColor: "var(--wofy-surface)",
                  }}
                >
                  {ev.admission === "Free" && (
                    <span className="absolute top-2 right-2 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "#065F46", color: "#D1FAE5" }}>
                      FREE
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: "var(--wofy-primary)" }}>
                    {fmt(ev.start_date)} · {ev.category}
                  </div>
                  <div className="mt-1 font-display text-base leading-tight line-clamp-2">
                    {ev.title}
                  </div>
                  <div className="mt-1 text-xs line-clamp-1 flex items-center gap-1" style={{ color: "var(--wofy-text-soft)" }}>
                    <MapPin className="w-3 h-3" /> {[ev.venue, ev.city].filter(Boolean).join(" · ")}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
